import { useEffect, useState } from "react";
import { Page, Skeleton } from "../components/ui.jsx";
import { LEVEL_LABEL, rankOf } from "../data/levels.js";
import { capabilitiesFor, capabilitySummary, diffTiers } from "../lib/capabilities.js";

const api = (path, opts) =>
  fetch("/api/admin/" + path, { headers: { "content-type": "application/json" }, ...opts });

function TierPicker({ value, onChange, levels }) {
  return (
    <div className="chips">
      {levels.map((l) => (
        <button
          key={l}
          className={"chip" + (value === l ? " on" : "")}
          onClick={() => onChange(l)}
        >
          {LEVEL_LABEL[l]}
        </button>
      ))}
    </div>
  );
}

// Read-only preview of everything a tier can and cannot reach.
function Preview({ level }) {
  const groups = capabilitiesFor(level);
  const { allowed, total } = capabilitySummary(level);

  return (
    <>
      <div className="journey-stats">
        <div className="ptext">
          <span>{LEVEL_LABEL[level]}</span>
          <span>
            {allowed} of {total} unlocked
          </span>
        </div>
        <div className="pbar">
          <div style={{ width: Math.round((allowed / total) * 100) + "%" }} />
        </div>
      </div>

      {groups.map((g) => (
        <div key={g.group} className="block">
          <h3>{g.group}</h3>
          {g.items.map((i) => (
            <div key={i.name} className="perm-row">
              <span className={"perm-dot " + (i.allowed ? "yes" : "no")}>
                {i.allowed ? "✓" : "—"}
              </span>
              <span className={i.allowed ? "" : "perm-off"}>{i.name}</span>
              {i.min !== "member" && <span className="lockbadge">{i.min}</span>}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

const ago = (ts) => {
  if (!ts) return "—";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// The sentinel for "nobody has decided about this person yet". Deliberately
// not a tier: it is the difference between someone put at the lowest tier on
// purpose and someone no list or override covers at all.
const UNASSIGNED = "unassigned";

// Everybody in the app: the union of the sign-in register and everyone a
// mapped People List grants a tier to.
//
// Both halves matter. The register is the only place email sign-ins Planning
// Center has never heard of can appear — they have no person id, so no PCO
// search will ever return them. The lists are the only place people who have
// been granted access but never opened the app appear. Either source alone
// makes this page lie, in a different direction each time.
function People({ levels }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [note, setNote] = useState("");

  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [link, setLink] = useState("all");
  const [seen, setSeen] = useState("all");

  const load = () =>
    api("people")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load people.");
        setRows(d.people || []);
      })
      .catch((e) => setError(String(e.message || e)));

  useEffect(() => {
    load();
  }, []);

  const setLevel = async (person, level) => {
    setSaving(person.key);
    setNote("");
    try {
      const r = await api("override", {
        method: "POST",
        body: JSON.stringify({ key: person.key, name: person.name, level }),
      });
      const d = await r.json();
      if (!r.ok) {
        setNote(d.error || "Could not save.");
      } else {
        await load();
        setNote(
          level === null
            ? `Cleared — ${person.name} follows the access lists again.`
            : `${person.name} is now ${LEVEL_LABEL[level]}. They must sign out and back in.`,
        );
      }
    } catch {
      setNote("Could not save.");
    }
    setSaving("");
  };

  /**
   * Grants or revokes "can file a report", which is a capability and not a
   * tier. It is the one thing on this screen that takes effect without a sign
   * out, because /api/me looks it up on every app open rather than reading it
   * off the session cookie.
   */
  const setReporter = async (person, on) => {
    setSaving(person.key);
    setNote("");
    try {
      const r = await api("filer", {
        method: "POST",
        body: JSON.stringify({ key: person.key, name: person.name, on }),
      });
      const d = await r.json();
      if (!r.ok) setNote(d.error || "Could not save.");
      else {
        await load();
        setNote(
          on
            ? `${person.name} can now file incident reports. They still cannot read any report back, and the change is live — no sign-out needed.`
            : `${person.name} can no longer file reports.`,
        );
      }
    } catch {
      setNote("Could not save.");
    }
    setSaving("");
  };

  if (error) {
    return (
      <div className="card locked">
        <div className="name">
          <span>🔒 {error}</span>
        </div>
      </div>
    );
  }
  if (!rows) return <Skeleton />;

  const needle = q.trim().toLowerCase();
  const shown = rows.filter((p) => {
    if (needle && !`${p.name} ${p.email} ${p.pid}`.toLowerCase().includes(needle)) {
      return false;
    }
    if (tier === UNASSIGNED && p.effective) return false;
    if (tier !== "all" && tier !== UNASSIGNED && p.effective !== tier) return false;
    if (seen === "yes" && !p.signedIn) return false;
    if (seen === "no" && p.signedIn) return false;
    if (link === "linked" && !p.linked) return false;
    if (link === "unlinked" && p.linked) return false;
    return true;
  });

  return (
    <>
      <div className="block">
        <h3>Everybody in the app</h3>
        <p>
          Everyone on a mapped access list, plus everyone who has signed in — including
          people <b>Planning Center has never heard of</b>, who can be given access here
          and connect an account later if they ever want to. <b>No tier</b> means no list
          and no override covers them yet.
        </p>
      </div>

      <input
        className="finput"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by name, email or ID…"
      />

      <div className="chips" style={{ marginTop: 10 }}>
        {["all", ...levels, UNASSIGNED].map((l) => (
          <button
            key={l}
            className={"chip" + (tier === l ? " on" : "")}
            onClick={() => setTier(l)}
          >
            {l === "all" ? "All tiers" : l === UNASSIGNED ? "No tier" : LEVEL_LABEL[l]}
          </button>
        ))}
      </div>
      <div className="chips" style={{ marginTop: 8 }}>
        {[
          ["all", "Everyone"],
          ["linked", "In Planning Center"],
          ["unlinked", "Email only"],
        ].map(([v, label]) => (
          <button
            key={v}
            className={"chip" + (link === v ? " on" : "")}
            onClick={() => setLink(v)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="chips" style={{ marginTop: 8 }}>
        {[
          ["all", "Signed in or not"],
          ["yes", "Has signed in"],
          ["no", "Never signed in"],
        ].map(([v, label]) => (
          <button
            key={v}
            className={"chip" + (seen === v ? " on" : "")}
            onClick={() => setSeen(v)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="empty-note">
        {shown.length} of {rows.length} {rows.length === 1 ? "person" : "people"}
      </div>
      {note && <div className="empty-note">{note}</div>}

      {rows.length === 0 && (
        <div className="empty-note">
          Nobody yet. This lists everyone on a mapped access list plus everyone who has
          signed in — so it stays empty until a People List is mapped under Access lists,
          or somebody signs in.
        </div>
      )}

      {shown.map((p) => (
        <div key={p.key} className="card">
          <div className="name">
            <span>{p.name}</span>
            {p.effective ? (
              <span className="badge">{p.effective}</span>
            ) : (
              <span className="lockbadge">no tier</span>
            )}
            {p.filer && <span className="badge rep">reporter</span>}
          </div>
          <div className="desc">
            {p.email || `ID ${p.pid}`}
            {!p.linked && " · not in Planning Center"}
            {p.source === "override" && " · override"}
            {p.source === "list" && " · from access list"}
            {p.source === "unassigned" && " · no list, no override"}
            {p.signedIn
              ? ` · ${p.count} sign-in${p.count === 1 ? "" : "s"} · last ${ago(p.last)}`
              : " · never signed in"}
          </div>

          {p.locked ? (
            <div className="empty-note">Set in Netlify config — not editable here.</div>
          ) : (
            <div className="chips" style={{ marginTop: 10 }}>
              {levels.map((lv) => (
                <button
                  key={lv}
                  className={"chip" + (p.override === lv ? " on" : "")}
                  disabled={saving === p.key}
                  onClick={() => setLevel(p, lv)}
                >
                  {LEVEL_LABEL[lv]}
                </button>
              ))}
              {p.override && (
                <button
                  className="chip"
                  disabled={saving === p.key}
                  onClick={() => setLevel(p, null)}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Separate row from the tier chips, because it is a different kind
              of thing. Offered even to somebody who is locked in Netlify
              config: that lock is on their tier, and this is not one. */}
          <div className="chips cap-row">
            <button
              className={"chip cap" + (p.filer ? " on" : "")}
              disabled={saving === p.key}
              onClick={() => setReporter(p, !p.filer)}
            >
              {p.filer ? "✓ Reporter" : "Reporter"}
            </button>
            <span className="cap-note">
              {rankOf(p.effective) >= rankOf("leader")
                ? "Already files by rank — this grant adds nothing."
                : "Can open the incident form. Cannot read any report back."}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * The link table.
 *
 * Every external address the app knows, in one place, editable without a
 * deploy. The ids come from src/data/*.js and are fixed; the URLs live in
 * Blobs and are what this screen writes. A blank row is a link nobody has set
 * up yet — the card still renders for whoever may see it and says so rather
 * than opening a dead page.
 *
 * The tier beside each row is read-only on purpose. It is the copy the server
 * enforces, and letting it be edited here would make the console the place
 * access quietly widens.
 */
function Links() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState("");
  const [note, setNote] = useState("");
  const [only, setOnly] = useState("all");

  const load = () =>
    // Not the admin helper: the link table is its own endpoint, and
    // "/api/admin/../links" only works by accident of URL normalisation.
    fetch("/api/links")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load the link table.");
        setRows(d.links || []);
      })
      .catch((e) => setError(String(e.message || e)));

  useEffect(() => {
    load();
  }, []);

  const save = async (row, override) => {
    const url = (override ?? draft[row.id] ?? row.url).trim();
    setSaving(row.id);
    setNote("");
    try {
      const r = await fetch("/api/links/set", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: row.id, url }),
      });
      const d = await r.json();
      if (!r.ok) setNote(d.error || "Could not save.");
      else {
        await load();
        setDraft((c) => {
          const next = { ...c };
          delete next[row.id];
          return next;
        });
        setNote(url ? `${row.id} set. Live immediately.` : `${row.id} cleared.`);
      }
    } catch {
      setNote("Could not save.");
    }
    setSaving("");
  };

  if (error) {
    return (
      <div className="card locked">
        <div className="name">
          <span>🔒 {error}</span>
        </div>
      </div>
    );
  }
  if (!rows) return <Skeleton />;

  const shown = rows.filter((r) =>
    only === "all" ? true : only === "missing" ? !r.configured : r.configured,
  );
  const missing = rows.filter((r) => !r.configured).length;

  return (
    <>
      <div className="block">
        <h3>🔗 External links</h3>
        <p>
          Every Drive folder and GroupMe room the app links to. These used to sit in
          the app's JavaScript, where anyone could read them without signing in —
          they now live here, and are handed out only after the server checks the
          person's tier. Paste a full <b>https://</b> address; it is live on the next
          tap, with no deploy.
        </p>
      </div>

      <div className="chips">
        {[
          ["all", `All ${rows.length}`],
          ["missing", `Not set up ${missing}`],
          ["set", `Set ${rows.length - missing}`],
        ].map(([v, label]) => (
          <button
            key={v}
            className={"chip" + (only === v ? " on" : "")}
            onClick={() => setOnly(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {note && <div className="empty-note">{note}</div>}

      {shown.map((row) => (
        <div key={row.id} className="card">
          <div className="name">
            <span>{row.id}</span>
            <span className={row.configured ? "badge" : "lockbadge"}>
              {row.configured ? row.minLevel : "not set up"}
            </span>
          </div>
          <input
            className="finput"
            style={{ marginTop: 10 }}
            value={draft[row.id] ?? row.url}
            placeholder="https://drive.google.com/drive/folders/…"
            onChange={(e) => setDraft({ ...draft, [row.id]: e.target.value })}
          />
          <div className="chips" style={{ marginTop: 10 }}>
            <button
              className="chip"
              disabled={saving === row.id || (draft[row.id] ?? row.url) === row.url}
              onClick={() => save(row)}
            >
              {saving === row.id ? "Saving…" : "Save"}
            </button>
            {row.configured && (
              <button
                className="chip"
                disabled={saving === row.id}
                onClick={() => save(row, "")}
              >
                Clear
              </button>
            )}
            <span className="cap-note">Visible at {row.minLevel} and above.</span>
          </div>
        </div>
      ))}
    </>
  );
}

// Who is actually in the tier being previewed. Reads the same union the People
// tab does, so the two can never disagree about who holds what.
function TierRoster({ level }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api("people")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load people.");
        setRows(d.people || []);
      })
      .catch((e) => setError(String(e.message || e)));
  }, []);

  if (error) return <div className="empty-note">{error}</div>;
  if (!rows) return <Skeleton />;

  const inTier =
    level === UNASSIGNED
      ? rows.filter((p) => !p.effective)
      : rows.filter((p) => p.effective === level);

  return (
    <>
      <div className="block" style={{ marginTop: 28 }}>
        <h3>
          Who is {level === UNASSIGNED ? "unassigned" : LEVEL_LABEL[level]} ·{" "}
          {inTier.length}
        </h3>
        {level === UNASSIGNED ? (
          <p>
            On no mapped access list and carrying no override. They are not locked
            out — tier resolution falls through to <b>{LEVEL_LABEL.member}</b>, so
            this is the list of people nobody has made a decision about yet.
          </p>
        ) : (
          <p>Counted from overrides first, then access lists — the same order sign-in uses.</p>
        )}
      </div>

      {inTier.length === 0 ? (
        <div className="empty-note">Nobody.</div>
      ) : (
        inTier.map((p) => (
          <div key={p.key} className="card">
            <div className="name">
              <span>{p.name}</span>
              <span className={p.signedIn ? "badge" : "lockbadge"}>
                {p.signedIn ? "signed in" : "never"}
              </span>
            </div>
            <div className="desc">
              {p.email || `ID ${p.pid}`}
              {p.source === "override" && " · override"}
              {p.source === "list" && " · from access list"}
            </div>
          </div>
        ))
      )}
    </>
  );
}

// Maps Planning Center People Lists to access tiers. Lists rather than Services
// teams, so that scheduling somebody onto a team can never change what they can
// reach — see netlify/lib/tiers.mts.
function AccessLists({ levels }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");

  const load = () =>
    api("lists")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) setError(d.error || "Could not read Planning Center lists.");
        setState(d);
      })
      .catch(() => setError("Could not read Planning Center lists."));

  useEffect(() => {
    load();
  }, []);

  const assign = async (list, tier) => {
    setSaving(list.id);
    try {
      await api("list-tier", {
        method: "POST",
        body: JSON.stringify({ listId: list.id, name: list.name, tier }),
      });
      await load();
    } catch {
      setError("Could not save.");
    }
    setSaving("");
  };

  return (
    <>
      <div className="block" style={{ marginTop: 32 }}>
        <h3>🗝️ Access lists</h3>
        <p>
          A Planning Center <b>People List</b> grants a tier to everyone on it. Lists are
          used rather than Services teams so that scheduling someone onto a team never
          changes their access. Unmapped lists grant nothing.
        </p>
      </div>

      {error && (
        <div className="card locked">
          <div className="name">
            <span>🔒 {error}</span>
          </div>
        </div>
      )}

      {!state ? (
        <Skeleton />
      ) : state.lists.length === 0 ? (
        <div className="empty-note">No People Lists found in Planning Center.</div>
      ) : (
        state.lists.map((l) => (
          <div key={l.id} className="card">
            <div className="name">
              <span>{l.name}</span>
              {l.tier ? (
                <span className="badge">{l.tier}</span>
              ) : (
                <span className="lockbadge">no access</span>
              )}
            </div>
            {l.count != null && <div className="desc">{l.count} people</div>}
            <div className="chips" style={{ marginTop: 10 }}>
              {levels.map((lv) => (
                <button
                  key={lv}
                  className={"chip" + (l.tier === lv ? " on" : "")}
                  disabled={saving === l.id}
                  onClick={() => assign(l, lv)}
                >
                  {LEVEL_LABEL[lv]}
                </button>
              ))}
              {l.tier && (
                <button
                  className="chip"
                  disabled={saving === l.id}
                  onClick={() => assign(l, null)}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </>
  );
}

export default function Admin({ back }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [person, setPerson] = useState(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");

  // Which tier the preview panel is showing.
  const [previewLevel, setPreviewLevel] = useState("member");

  // The register of everyone who has signed in leads, because it is the only
  // view that can reach people Planning Center does not know about.
  const [tab, setTab] = useState("people");

  const load = () =>
    api("state")
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        setState(d);
        setLoading(false);
      })
      .catch(async (r) => {
        const body = await r.json?.().catch(() => ({}));
        setError(body?.error || "Could not load admin state.");
        setLoading(false);
      });

  useEffect(() => {
    load();
  }, []);

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    setResults(null);
    try {
      const r = await api("search?q=" + encodeURIComponent(query.trim()));
      const d = await r.json();
      setResults(d.people || []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const choose = (p) => {
    setPerson(p);
    const existing = state?.overrides?.find((o) => o.pid === p.pid);
    const level = existing?.level || "member";
    setPending(level);
    setPreviewLevel(level);
    setSaved("");
  };

  const save = async (level) => {
    if (!person) return;
    setSaving(true);
    setSaved("");
    try {
      const r = await api("override", {
        method: "POST",
        body: JSON.stringify({ pid: person.pid, name: person.name, level }),
      });
      const d = await r.json();
      if (!r.ok) {
        setSaved(d.error || "Could not save.");
      } else {
        setState((s) => ({ ...s, overrides: d.overrides }));
        setSaved(
          level === null
            ? `Cleared — ${person.name} now follows Planning Center.`
            : `${person.name} is now ${LEVEL_LABEL[level]}. They must sign out and back in.`,
        );
      }
    } catch {
      setSaved("Could not save.");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Page back={back} eyebrow="Access Control" title="Roles.">
        <Skeleton />
      </Page>
    );
  }

  if (error) {
    return (
      <Page back={back} eyebrow="Access Control" title="Roles.">
        <div className="card locked">
          <div className="name">
            <span>🔒 {error}</span>
          </div>
        </div>
      </Page>
    );
  }

  const levels = state.levels;
  const current = person
    ? state.overrides.find((o) => o.pid === person.pid)?.level || "member"
    : null;
  const delta = person && pending ? diffTiers(current, pending) : null;

  return (
    <Page
      back={back}
      eyebrow="Access Control"
      title="Roles."
      intro="Assign a tier to a person and see exactly what it opens up. Overrides win over Planning Center team membership."
    >
      <div className="chips">
        {[
          ["people", "👥 People"],
          ["assign", "🔎 Find in PCO"],
          ["lists", "🗝️ Access lists"],
          ["preview", "👁 Tier preview"],
          ["links", "🔗 Links"],
        ].map(([v, label]) => (
          <button
            key={v}
            className={"chip" + (tab === v ? " on" : "")}
            onClick={() => setTab(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "people" && <People levels={levels} />}
      {tab === "links" && <Links />}

      {tab === "assign" && (
      <>
      {/* ---- 1. pick a person ---- */}
      <div className="block">
        <h3>1 · Find a person</h3>
        <p>Searches Planning Center People.</p>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="finput"
          style={{ marginTop: 0 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Name or email…"
        />
        <button className="btn-post" onClick={search} disabled={query.trim().length < 2}>
          {searching ? "…" : "Search"}
        </button>
      </div>

      {results && results.length === 0 && (
        <div className="empty-note">No matches in Planning Center.</div>
      )}
      {results &&
        results.map((p) => (
          <button key={p.pid} className="card" onClick={() => choose(p)}>
            <div className="name">
              <span>{p.name}</span>
              <span className="arrow">{person?.pid === p.pid ? "✓" : "→"}</span>
            </div>
            <div className="desc">ID {p.pid}</div>
          </button>
        ))}

      {/* ---- 2. assign ---- */}
      {person && (
        <>
          <div className="block" style={{ marginTop: 28 }}>
            <h3>2 · {person.name}</h3>
            <p>
              Currently <b>{LEVEL_LABEL[current]}</b>
              {current === "member" && " (no override — following Planning Center)"}.
            </p>
          </div>
          <TierPicker
            value={pending}
            onChange={(l) => {
              setPending(l);
              setPreviewLevel(l);
            }}
            levels={levels}
          />

          {delta && (delta.gained.length > 0 || delta.lost.length > 0) && (
            <div className="checkin-reply">
              {delta.gained.length > 0 && (
                <div>
                  <b>Gains:</b> {delta.gained.join(", ")}
                </div>
              )}
              {delta.lost.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <b>Loses:</b> {delta.lost.join(", ")}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn-post" onClick={() => save(pending)} disabled={saving}>
              {saving ? "Saving…" : `Set to ${LEVEL_LABEL[pending]}`}
            </button>
            <button
              className="lockout"
              style={{ marginTop: 0 }}
              onClick={() => save(null)}
              disabled={saving}
            >
              Clear override
            </button>
          </div>
          {saved && <div className="empty-note">{saved}</div>}
        </>
      )}

      {/* ---- 3. current overrides ---- */}
      <div className="block" style={{ marginTop: 32 }}>
        <h3>Current overrides</h3>
        <p>
          Anyone not listed here gets their tier from the access lists above.
        </p>
      </div>
      {state.overrides.length === 0 ? (
        <div className="empty-note">No overrides set.</div>
      ) : (
        state.overrides.map((o) => (
          <div key={o.pid} className="card">
            <div className="name">
              <span>{o.name || `Person ${o.pid}`}</span>
              <span className="badge">{o.level}</span>
            </div>
            <div className="desc">
              ID {o.pid}
              {o.source === "env" && " · set in Netlify config, not editable here"}
              {o.source === "code" && " · seeded in code, not editable here"}
              {o.shadowedByEnv && " · a higher layer is winning over this entry"}
              {o.setBy && ` · by ${o.setBy}`}
            </div>
          </div>
        ))
      )}
      </>
      )}

      {tab === "lists" && <AccessLists levels={levels} />}

      {tab === "preview" && (
        <>
          <div className="block">
            <h3>What each tier can see</h3>
            <p>
              Pick a tier to preview what it opens and who currently holds it. The
              capability list is generated from the app's real gates.
            </p>
          </div>
          <div className="chips">
            {[...levels, UNASSIGNED].map((l) => (
              <button
                key={l}
                className={"chip" + (previewLevel === l ? " on" : "")}
                onClick={() => setPreviewLevel(l)}
              >
                {l === UNASSIGNED ? "No tier" : LEVEL_LABEL[l]}
              </button>
            ))}
          </div>
          <TierRoster level={previewLevel} />
          {/* Unassigned people resolve to member at sign-in, so that is the
              capability set worth showing them against. */}
          <Preview level={previewLevel === UNASSIGNED ? "member" : previewLevel} />
        </>
      )}

      <div className="privacy-note">
        Tiers are baked into the session cookie at sign-in, so anyone you change must sign
        out and back in before it takes effect.
      </div>
    </Page>
  );
}
